import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Button } from '@clef/client-library';
import { useCreateProjectFolderMutation } from '@/serverStore/projectFolders';
import { useSnackbar } from 'notistack';
import { isValidProjectName } from '@/components/Utils/Validation';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: 400,
  },
  textField: {
    marginTop: theme.spacing(2),
  },
}));

interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  parentFolderId: number | null;
}

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  open,
  onClose,
  parentFolderId,
}) => {
  const styles = useStyles();
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const createFolderMutation = useCreateProjectFolderMutation();

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFolderName('');
      setError(null);
    }
  }, [open]);

  const handleCreate = useCallback(async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      setError(t('Folder name is required'));
      return;
    }

    if (!isValidProjectName(trimmedName)) {
      setError(t('Folder name can only contain letters, numbers, dashes, underscores and spaces'));
      return;
    }

    try {
      await createFolderMutation.mutateAsync({
        name: trimmedName,
        parentFolderId,
      });
      enqueueSnackbar(t('Folder created successfully'), { variant: 'success' });
      onClose();
    } catch (err: any) {
      const errorMessage = err?.body?.message || err?.message || t('Failed to create folder');
      setError(errorMessage);
    }
  }, [folderName, parentFolderId, createFolderMutation, enqueueSnackbar, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && folderName.trim()) {
        handleCreate();
      }
    },
    [handleCreate, folderName],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: styles.dialogPaper }}
      data-testid="create-folder-dialog"
    >
      <DialogTitle disableTypography>
        <Typography variant="h3">{t('Create New Folder')}</Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label={t('Folder name')}
          value={folderName}
          onChange={e => {
            setFolderName(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error}
          className={styles.textField}
          data-testid="folder-name-input"
        />
      </DialogContent>
      <DialogActions>
        <Button id="cancel-create-folder" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          id="confirm-create-folder"
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!folderName.trim() || createFolderMutation.isLoading}
        >
          {t('Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFolderDialog;
